import React from "react";
import { AeraButton } from "@/app/components/ui/AeraButton";
import Modal, { ModalFooter, ModalHeader } from "@/app/components/ui/Modal";
import { showToast } from "@/app/components/ui/Toast";

interface ResponseModalProps {
  isOpen: boolean;
  onClose: () => void;
  response: any;
  success: boolean;
  onReloadChat?: () => void;
  isEmbeddedInModal?: boolean;
}

const XIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const CheckCircleIcon = ({ size = 24 }: { size?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const AlertCircleIcon = ({ size = 24 }: { size?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" x2="12" y1="8" y2="12" />
    <line x1="12" x2="12.01" y1="16" y2="16" />
  </svg>
);

const ExternalLinkIcon = ({
  size = 14,
  className = "",
}: {
  size?: number;
  className?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" x2="21" y1="14" y2="3" />
  </svg>
);

export const ResponseModal: React.FC<ResponseModalProps> = ({
  isOpen,
  onClose,
  response,
  success,
  onReloadChat,
  isEmbeddedInModal = false,
}) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    if (response?.line_webhook_url) {
      try {
        await navigator.clipboard.writeText(response.line_webhook_url);
        setCopied(true);
        showToast("คัดลอกแล้ว");
        setTimeout(() => setCopied(false), 3000);
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    }
  };

  const headerTone = response?.line_webhook_url
    ? "bg-indigo-50 border-indigo-100"
    : success
      ? "bg-aera-greenlight/40 border-aera-greenlight"
      : "bg-aera-redlight border-aera-red/20";

  const headerTitleClass = response?.line_webhook_url
    ? "text-indigo-900"
    : success
      ? "text-aera-green"
      : "text-aera-red";

  const modalHeader = (
    <ModalHeader surface={false} className={headerTone}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {response?.line_webhook_url ? (
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-aera-white">
              <ExternalLinkIcon />
            </div>
          ) : success ? (
            <CheckCircleIcon size={24} />
          ) : (
            <AlertCircleIcon size={24} />
          )}
          <h3 className={`truncate font-bold ${headerTitleClass}`}>
            {response?.line_webhook_url
              ? "LINE Webhook"
              : success
                ? "Success"
                : "Error"}
          </h3>
        </div>
        <AeraButton
          variant="ghost"
          size="xs"
          type="button"
          onClick={onClose}
          className="text-aera-gray transition-colors hover:text-aera-charcoal p-1! h-auto! w-auto! min-w-0!"
        >
          <XIcon />
        </AeraButton>
      </div>
    </ModalHeader>
  );

  const footer = response?.line_webhook_url ? (
    <AeraButton variant="outline" fontWeight="medium" onClick={onClose}>
      ปิด
    </AeraButton>
  ) : success ? (
    <AeraButton
      variant="primary"
      fontWeight="medium"
      onClick={() => {
        onReloadChat?.();
        onClose();
      }}
    >
      เริ่มแชท
    </AeraButton>
  ) : (
    <AeraButton variant="outline" fontWeight="medium" onClick={onClose}>
      ปิด
    </AeraButton>
  );

  const responseBody = response ? (
    <div className="space-y-4">
      <div className="mb-6 flex justify-center">
        {success ? (
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-aera-greenlight/40 text-aera-black">
            <CheckCircleIcon size={40} />
          </div>
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-aera-redlight text-aera-white">
            <AlertCircleIcon size={40} />
          </div>
        )}
      </div>

      {response.line_webhook_url ? (
        <div>
          <h4 className="mb-2 text-sm font-bold text-aera-charcoal">
            LINE Webhook URL
          </h4>
          <div className="flex items-start gap-2">
            <p className="flex-1 break-all rounded bg-aera-white p-3 text-sm text-aera-charcoal">
              {response.line_webhook_url}
            </p>
            <AeraButton
              variant="secondary"
              size="sm"
              onClick={handleCopy}
              className={copied ? "bg-aera-greendark!" : ""}
            >
              {copied ? "Copied!" : "Copy"}
            </AeraButton>
          </div>
        </div>
      ) : success ? (
        <div className="text-center">
          <p className="text-lg font-bold text-aera-green">
            {response.status || "Success"}
          </p>
        </div>
      ) : (
        <div className="text-center">
          <p className="text-sm text-aera-red">
            {response.error || response.message || "An error occurred"}
          </p>
        </div>
      )}
    </div>
  ) : null;

  if (isEmbeddedInModal) {
    if (!isOpen) return null;
    return (
      <>
        {responseBody}
        <ModalFooter>{footer}</ModalFooter>
      </>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={modalHeader}
      footer={footer}
      size="lg"
    >
      {responseBody}
    </Modal>
  );
};
